import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import api, { fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { RESTRICTION_MAP } from "../constants/restrictions";
import { ArrowRight, CheckCircle2, MapPin, BookOpen, ExternalLink } from "lucide-react";

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "s", "ol", "ul", "li", "h2", "h3", "h4", "a", "blockquote"],
  ALLOWED_ATTR: ["href", "target", "rel"],
};

function SanitizedHTML({ html, className }) {
  const clean = useMemo(() => DOMPurify.sanitize(html || "", SANITIZE_CONFIG), [html]);
  return <div className={className} dangerouslySetInnerHTML={{ __html: clean }} />;
}

export default function Topic() {
  const { id } = useParams();
  const [topic, setTopic] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setTopic(null); setNotFound(false);
    api.get(`/topics/${id}`).then((r) => setTopic(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto py-24 text-center">
        <h1 className="font-heading font-black text-3xl text-brand-text">Tópico não encontrado</h1>
        <Link to="/" className="inline-block mt-6 text-brand-primary font-bold hover:underline">Voltar para o início</Link>
      </div>
    );
  }
  if (!topic) return <div className="max-w-6xl mx-auto p-10">Carregando...</div>;

  const meta = RESTRICTION_MAP[topic.topic_id];
  const image = topic.image_path ? fileUrl(topic.image_path) : null;
  const sections = topic.sections || [];
  const gallery = topic.gallery || [];
  const links = topic.links || [];

  return (
    <div data-testid={`topic-page-${topic.topic_id}`}>
      {/* HERO */}
      <section className="relative border-b border-brand-border overflow-hidden" style={{ background: `linear-gradient(135deg, ${meta?.bg || "#F4EDE0"} 0%, #FDFBF7 60%)` }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-3">
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.18em]" style={{ background: meta?.bg, color: meta?.text }}>
              <BookOpen className="w-3.5 h-3.5" /> Guia informativo
            </span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-brand-text mt-4 leading-tight">
              {meta?.emoji} {topic.title}
            </h1>
            {topic.subtitle && <p className="mt-4 text-xl text-brand-textMuted font-bold">{topic.subtitle}</p>}
            <Link to={`/explorar?restriction=${topic.topic_id}`} className="inline-block mt-8">
              <Button className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-6 py-6 text-base">
                <MapPin className="w-5 h-5 mr-2" /> Ver lugares seguros <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="md:col-span-2">
            {image ? (
              <div className="aspect-square rounded-3xl overflow-hidden shadow-lg border border-brand-border">
                <img src={image} alt={topic.title} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="aspect-square rounded-3xl flex items-center justify-center text-8xl shadow-inner" style={{ background: meta?.bg }}>{meta?.emoji}</div>
            )}
          </div>
        </div>
      </section>

      {/* CONTEÚDO */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-14">
        {/* Descrição (rich text) */}
        {topic.description && (
          <div data-testid="topic-description-block">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-brand-text mb-4">O que é</h2>
            <SanitizedHTML html={topic.description} className="prose prose-lg max-w-none text-brand-text/90 leading-relaxed [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-2xl [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-xl [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-brand-primary [&_a]:underline [&_strong]:font-bold [&_em]:italic [&_blockquote]:border-l-4 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:border-brand-primary" />
          </div>
        )}

        {/* Seções livres */}
        {sections.map((s, i) => (
          <div key={`section-${i}`} data-testid={`section-block-${i}`}>
            {s.heading && <h2 className="font-heading font-black text-2xl sm:text-3xl text-brand-text mb-4">{s.heading}</h2>}
            {s.image_path && (
              <div className="rounded-3xl overflow-hidden border border-brand-border mb-5 max-h-80">
                <img src={fileUrl(s.image_path)} alt={s.heading} className="w-full h-full object-cover" />
              </div>
            )}
            {s.body && <SanitizedHTML html={s.body} className="prose prose-lg max-w-none text-brand-text/90 leading-relaxed [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-2xl [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-xl [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-brand-primary [&_a]:underline [&_strong]:font-bold [&_em]:italic [&_blockquote]:border-l-4 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:border-brand-primary" />}
          </div>
        ))}

        {/* Dicas */}
        {topic.tips && topic.tips.length > 0 && (
          <div>
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-brand-text mb-6">Dicas de cuidado</h2>
            <ul className="space-y-4">
              {topic.tips.map((tip, i) => (
                <li key={`tip-${i}-${tip.slice(0, 20)}`} className="flex items-start gap-3 bg-white rounded-2xl border border-brand-border p-5 shadow-sm">
                  <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: meta?.text || "#2A5C43" }} />
                  <span className="text-brand-text leading-relaxed">{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Galeria */}
        {gallery.length > 0 && (
          <div data-testid="topic-gallery">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-brand-text mb-6">Galeria</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {gallery.map((p, i) => (
                <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" className="aspect-square rounded-2xl overflow-hidden border border-brand-border hover:scale-[1.02] transition-transform">
                  <img src={fileUrl(p)} alt={`galeria ${i + 1}`} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Links úteis */}
        {links.length > 0 && (
          <div data-testid="topic-links">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-brand-text mb-6">Links úteis</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {links.map((l, i) => (
                <li key={`link-${i}-${l.url}`}>
                  <a href={l.url} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-3 bg-white rounded-2xl border border-brand-border p-4 hover:border-brand-primary transition-colors">
                    <span className="font-bold text-brand-text">{l.label}</span>
                    <ExternalLink className="w-4 h-4 text-brand-textMuted shrink-0" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA */}
        <div className="rounded-3xl p-8 text-center border-2" style={{ borderColor: meta?.text, background: meta?.bg }}>
          <h3 className="font-heading font-black text-2xl text-brand-text">Encontrou um lugar seguro?</h3>
          <p className="text-brand-text/80 mt-2">Ajude a comunidade cadastrando restaurantes, cafés e padarias que atendem {topic.title.toLowerCase()}.</p>
          <Link to="/cadastrar-lugar" className="inline-block mt-5">
            <Button className="rounded-full bg-white hover:bg-brand-bg font-bold px-6" style={{ color: meta?.text }}>Indicar um lugar</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
